import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import axiosInstance from '../utils/axiosConfig';
import LoadingSpinner from './LoadingSpinner';
import ExportButton from './ExportButton';
import { exportRenderedTableData } from '../utils/exportUtils';
import { useToast } from '../utils/toastContext';
import '../styles/theme.css';
import '../styles/ViewAdminSchedules.css';
import '../styles/ViewSchedulesModern.css';

const VIEW_BY_OPTIONS = [
    { value: 'speciality', label: 'Rotation/Experience' },
    { value: 'institution', label: 'Institution' },
    { value: 'preceptor', label: 'Preceptor' },
    { value: 'student', label: 'Student' }
];

const ROW_LABELS = {
    speciality: 'Rotation/Experience',
    institution: 'Institution',
    preceptor: 'Preceptor',
    student: 'Student'
};

const palette = {
    pageBg: '#f6f8fb',
    border: '#d9e1ec',
    mutedBorder: '#e7edf5',
    panelTint: '#f3f7fd',
    accent: '#2f5f8f',
    accentDark: '#22476b',
    accentSoft: '#eaf1fb',
    text: '#1b2a3a',
    secondaryText: '#5a6b7f',
    primary: '#1f4e79',
    primarySoft: '#e8f0fa',
    modalBackdrop: 'rgba(12, 20, 33, 0.46)'
};

const tableStyles = {
    wrapper: {
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
        borderRadius: '14px',
        border: `1px solid ${palette.border}`,
        borderTop: `3px solid ${palette.accent}`,
        boxShadow: '0 10px 24px rgba(18, 34, 58, 0.08)',
        overflow: 'hidden',
        marginBottom: '1.5rem'
    },
    tableContainer: {
        width: '100%',
        background: `linear-gradient(180deg, ${palette.panelTint} 0%, ${palette.pageBg} 100%)`,
        padding: '0.7rem',
        overflowX: 'auto'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
        background: '#ffffff',
        border: `1px solid ${palette.border}`
    },
    axisCell: {
        textAlign: 'left',
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`,
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.45rem 0.6rem',
        borderRight: 'none',
        borderBottom: 'none'
    },
    axisNumbersLabel: {
        textAlign: 'center',
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`,
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.45rem 0.35rem',
        borderRight: '1px solid rgba(255, 255, 255, 0.18)',
        borderBottom: 'none'
    },
    axisNumbersBlock: {
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`,
        borderLeft: 'none',
        borderRight: '1px solid rgba(255, 255, 255, 0.18)',
        borderBottom: 'none',
        padding: 0,
        verticalAlign: 'top'
    },
    axisNumbersBlockTitle: {
        textAlign: 'center',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        margin: 0,
        padding: '0.45rem 0.35rem 0.32rem',
        borderBottom: 'none'
    },
    axisNumbersBlockGrid: {
        display: 'grid',
        gap: 0,
        alignItems: 'stretch',
        borderTop: 'none',
        borderBottom: 'none'
    },
    axisNumbersBlockItem: {
        textAlign: 'center',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.16rem 0.35rem 0.22rem',
        borderLeft: '1px solid rgba(255, 255, 255, 0.18)',
        borderTop: 'none',
        borderBottom: 'none',
        boxSizing: 'border-box'
    },
    headerShell: {
        padding: 0,
        border: 'none',
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`
    },
    headerShellGrid: {
        display: 'grid',
        alignItems: 'stretch',
        width: '100%'
    },
    headerLeftBlock: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.45rem 0.6rem',
        borderRight: '1px solid rgba(255, 255, 255, 0.18)',
        alignSelf: 'stretch',
        height: '100%',
        boxSizing: 'border-box'
    },
    headerRightBlock: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        alignSelf: 'stretch',
        height: '100%',
        boxSizing: 'border-box'
    },
    headerRightTitle: {
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.45rem 0.35rem 0.32rem',
        textAlign: 'center',
        boxSizing: 'border-box'
    },
    headerRightNumbers: {
        display: 'grid',
        alignItems: 'stretch',
        boxSizing: 'border-box'
    },
    headerRightNumber: {
        color: '#ffffff',
        fontWeight: 700,
        fontSize: '0.76rem',
        letterSpacing: '0.03em',
        textTransform: 'uppercase',
        padding: '0.16rem 0.35rem 0.22rem',
        textAlign: 'center',
        boxSizing: 'border-box',
        borderLeft: '1px solid rgba(255, 255, 255, 0.18)'
    },
    rowHeader: {
        padding: '0.6rem 0.7rem',
        color: palette.text,
        fontSize: '0.86rem',
        fontWeight: 600,
        borderRight: `1px solid ${palette.border}`,
        borderBottom: `1px solid ${palette.mutedBorder}`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: '#ffffff'
    },
    dataCell: {
        borderBottom: `1px solid ${palette.mutedBorder}`,
        borderRight: `1px solid ${palette.mutedBorder}`,
        padding: '0.15rem'
    }
};

const modalStyles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: `linear-gradient(180deg, ${palette.modalBackdrop} 0%, rgba(12, 20, 33, 0.56) 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 30000,
        padding: '1rem'
    },
    card: {
        width: 'min(980px, 94vw)',
        maxHeight: '86vh',
        overflow: 'hidden',
        borderRadius: '14px',
        border: `1px solid ${palette.border}`,
        background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
        boxShadow: '0 28px 66px rgba(4, 18, 36, 0.28)',
        display: 'flex',
        flexDirection: 'column'
    },
    head: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.9rem 1rem',
        borderBottom: `1px solid ${palette.mutedBorder}`,
        background: `linear-gradient(90deg, ${palette.accentDark} 0%, ${palette.accent} 100%)`
    },
    headTitle: {
        margin: 0,
        fontSize: '1.02rem',
        color: '#ffffff',
        fontWeight: 700
    },
    headSub: {
        margin: '0.22rem 0 0',
        fontSize: '0.84rem',
        color: '#d8e8fb',
        fontWeight: 500
    },
    closeButton: {
        border: '1px solid rgba(255, 255, 255, 0.38)',
        background: 'rgba(255, 255, 255, 0.14)',
        color: '#ffffff',
        borderRadius: '999px',
        width: '32px',
        height: '32px',
        cursor: 'pointer',
        fontSize: '1.28rem',
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        flexShrink: 0
    },
    summary: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
        gap: '0.65rem',
        padding: '0.8rem 1.1rem',
        borderBottom: `1px solid ${palette.mutedBorder}`,
        background: `linear-gradient(180deg, ${palette.accentSoft} 0%, #f8fbff 100%)`
    },
    summaryCard: {
        border: '1px solid #d6e3f4',
        borderRadius: '8px',
        padding: '0.55rem 0.65rem',
        background: '#ffffff'
    },
    summaryLabel: {
        fontSize: '0.74rem',
        color: palette.secondaryText,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        marginBottom: '0.18rem',
        fontWeight: 700
    },
    summaryValue: {
        color: palette.text,
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.2,
        wordBreak: 'break-word'
    },
    body: {
        padding: '1rem 1.1rem 1.1rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
        background: 'linear-gradient(180deg, #fcfdff 0%, #f8fbff 100%)'
    },
    section: {
        marginBottom: '0.9rem'
    },
    sectionTitle: {
        margin: '0 0 0.45rem',
        color: '#334155',
        fontSize: '0.84rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.02em'
    },
    preceptorCard: {
        border: `1px solid ${palette.mutedBorder}`,
        borderRadius: '10px',
        marginBottom: '0.75rem',
        overflow: 'hidden',
        background: '#ffffff'
    },
    preceptorHead: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.8rem',
        background: 'linear-gradient(180deg, #f8fbff 0%, #f4f8fe 100%)',
        padding: '0.7rem 0.85rem',
        borderBottom: `1px solid ${palette.mutedBorder}`
    },
    preceptorName: {
        margin: 0,
        fontSize: '0.9rem',
        color: palette.text,
        fontWeight: 700
    },
    availabilityBadge: {
        background: `linear-gradient(180deg, ${palette.primarySoft} 0%, #dce8f7 100%)`,
        border: `1px solid ${palette.border}`,
        borderRadius: '6px',
        padding: '0.2rem 0.45rem',
        color: palette.primary,
        fontSize: '0.78rem',
        fontWeight: 700
    },
    studentsBox: {
        padding: '0.75rem 0.85rem'
    },
    studentsLabel: {
        margin: 0,
        fontSize: '0.76rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: palette.secondaryText
    },
    studentsList: {
        margin: '0.55rem 0 0',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.45rem'
    },
    studentChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem',
        borderRadius: '999px',
        border: `1px solid ${palette.border}`,
        background: '#ffffff',
        color: palette.text,
        padding: '0.26rem 0.55rem',
        fontSize: '0.77rem',
        fontWeight: 600
    },
    emptyState: {
        color: '#64748b',
        fontSize: '0.95rem',
        padding: '0.25rem 0'
    }
};

const hasPopupData = (popup) => {
    if (!popup || typeof popup !== 'object') {
        return false;
    }
    if (Array.isArray(popup.sections) && popup.sections.some((section) => (section?.preceptors || []).length > 0)) {
        return true;
    }
    if (Array.isArray(popup.students) && popup.students.length > 0) {
        return true;
    }
    if (popup.preceptor?.name || popup.coPreceptor?.name) {
        return true;
    }
    return false;
};

const renderStudentChips = (students = []) => {
    if (!students.length) {
        return <p style={modalStyles.emptyState}>No students assigned.</p>;
    }

    return (
        <div style={modalStyles.studentsList}>
            {students.map((student, index) => (
                <span key={`${student.studentId || student.name}-${index}`} style={modalStyles.studentChip}>
                    <span>{student.name || 'Unknown student'}</span>
                    <span style={{ fontSize: '0.72rem', color: student.isCoprecepted ? '#7c2d12' : '#475569', fontWeight: 700, textTransform: 'uppercase' }}>
                        {student.isCoprecepted ? 'Co-Preceptor' : 'Main Preceptor'}
                    </span>
                </span>
            ))}
        </div>
    );
};

const getPopupTitle = (popupType) => {
    switch (popupType) {
        case 'speciality':
            return 'Rotation / Experience Details';
        case 'institution':
            return 'Institution Details';
        case 'preceptor':
            return 'Preceptor Details';
        case 'student':
            return 'Student Details';
        default:
            return 'Schedule Details';
    }
};

const getPopupEntityLabel = (popupType) => {
    switch (popupType) {
        case 'speciality':
            return 'Experience';
        case 'institution':
            return 'Institution';
        case 'preceptor':
            return 'Preceptor';
        case 'student':
            return 'Student';
        default:
            return 'Item';
    }
};

const renderGroupedPopup = (popup) => {
    const sections = popup?.sections || [];
    if (!sections.length) {
        return <div style={modalStyles.emptyState}>No details available for this cell.</div>;
    }

    return (
        <div>
            {sections.map((section) => (
                <div key={section.title} style={modalStyles.section}>
                    <h5 style={modalStyles.sectionTitle}>{section.title}</h5>
                    {(section.preceptors || []).map((preceptor) => (
                        <section key={preceptor.preceptorId || preceptor.preceptorName} style={modalStyles.preceptorCard}>
                            <header style={modalStyles.preceptorHead}>
                                <h6 style={modalStyles.preceptorName}>{preceptor.preceptorName || 'Unknown preceptor'}</h6>
                                <span style={modalStyles.availabilityBadge}>
                                    {preceptor.availability || 0} slot{(preceptor.availability || 0) === 1 ? '' : 's'}
                                </span>
                            </header>
                            <div style={modalStyles.studentsBox}>
                                <p style={modalStyles.studentsLabel}>Assigned Students</p>
                                {renderStudentChips(preceptor.students || [])}
                            </div>
                        </section>
                    ))}
                </div>
            ))}
        </div>
    );
};

const renderPreceptorPopup = (popup) => (
    <section style={modalStyles.preceptorCard}>
        <header style={modalStyles.preceptorHead}>
            <div>
                <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#cfe0fb' }}>
                    Preceptor
                </p>
                <h6 style={modalStyles.preceptorName}>{popup.preceptorName || 'Unknown preceptor'}</h6>
            </div>
            <span style={modalStyles.availabilityBadge}>
                {popup.availability || 0} slot{(popup.availability || 0) === 1 ? '' : 's'}
            </span>
        </header>
        <div style={modalStyles.studentsBox}>
            <p style={modalStyles.studentsLabel}>Assigned Students</p>
            {renderStudentChips(popup.students || [])}
        </div>
    </section>
);

const renderStudentPopup = (popup) => (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
        <section style={modalStyles.preceptorCard}>
            <header style={modalStyles.preceptorHead}>
                <h6 style={modalStyles.preceptorName}>Primary Preceptor</h6>
            </header>
            <div style={modalStyles.studentsBox}>
                <p style={modalStyles.studentsLabel}>Preceptor</p>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.95rem', fontWeight: 700, color: '#1b2a3a' }}>{popup.preceptor?.name || 'Unassigned'}</p>
                <p style={{ margin: '0.45rem 0 0', fontSize: '0.83rem', color: '#475569' }}>
                    Experience: {popup.preceptor?.specialityName || 'Unassigned'}
                </p>
            </div>
        </section>

        <section style={modalStyles.preceptorCard}>
            <header style={modalStyles.preceptorHead}>
                <h6 style={modalStyles.preceptorName}>Co-Preceptor</h6>
            </header>
            <div style={modalStyles.studentsBox}>
                <p style={modalStyles.studentsLabel}>Preceptor</p>
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.95rem', fontWeight: 700, color: '#1b2a3a' }}>{popup.coPreceptor?.name || 'Unassigned'}</p>
            </div>
        </section>
    </div>
);

const ScheduleReportingPage = () => {
    const { showError } = useToast();
    const [curriculums, setCurriculums] = useState([]);
    const [loadingCurriculums, setLoadingCurriculums] = useState(false);
    const [selectedCurriculum, setSelectedCurriculum] = useState('');
    const [viewBy, setViewBy] = useState('speciality');
    const [loadingReport, setLoadingReport] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [entityOptions, setEntityOptions] = useState([]);
    const [loadingEntityOptions, setLoadingEntityOptions] = useState(false);
    const [selectedEntityIds, setSelectedEntityIds] = useState([]);

    const isEntityFilterable = viewBy === 'student' || viewBy === 'preceptor';

    const selectedCurriculumOption = useMemo(
        () => curriculums.find((curriculum) => curriculum._id === selectedCurriculum) || null,
        [curriculums, selectedCurriculum]
    );

    useEffect(() => {
        const fetchCurriculums = async () => {
            setLoadingCurriculums(true);
            try {
                const response = await axiosInstance.get('/curriculum/curriculums');
                const items = response.data || [];
                setCurriculums(items);
                if (!selectedCurriculum && items.length > 0) {
                    setSelectedCurriculum(items[0]._id);
                }
            } catch {
                setCurriculums([]);
                showError('Failed to load curriculums for reporting.');
            } finally {
                setLoadingCurriculums(false);
            }
        };

        fetchCurriculums();
    }, [selectedCurriculum, showError]);

    // Reset the entity filter whenever the curriculum or dimension changes —
    // a previously selected student/preceptor may not exist in the new context.
    useEffect(() => {
        setSelectedEntityIds([]);
    }, [selectedCurriculum, viewBy]);

    useEffect(() => {
        if (!selectedCurriculum || !isEntityFilterable) {
            setEntityOptions([]);
            return;
        }

        const fetchEntityOptions = async () => {
            setLoadingEntityOptions(true);
            try {
                const path = viewBy === 'student' ? 'students' : 'preceptors';
                const response = await axiosInstance.get(`/curriculum/${encodeURIComponent(selectedCurriculum)}/${path}`);
                const list = Array.isArray(response.data)
                    ? response.data
                    : Array.isArray(response.data?.[path])
                        ? response.data[path]
                        : [];
                const options = list
                    .map((item) => {
                        const id = item.studentId ?? item.preceptorId ?? item._id;
                        const label = `${item.firstname || ''} ${item.lastname || ''}`.trim() || id;
                        return id ? { value: String(id), label } : null;
                    })
                    .filter(Boolean)
                    .sort((a, b) => a.label.localeCompare(b.label));
                setEntityOptions(options);
            } catch {
                setEntityOptions([]);
                showError(`Failed to load ${viewBy}s for filtering.`);
            } finally {
                setLoadingEntityOptions(false);
            }
        };

        fetchEntityOptions();
    }, [selectedCurriculum, viewBy, isEntityFilterable, showError]);

    useEffect(() => {
        if (!selectedCurriculum || !viewBy) {
            setReportData(null);
            return;
        }

        const fetchReport = async () => {
            setLoadingReport(true);
            try {
                const params = {
                    curriculum_id: selectedCurriculum,
                    view_by: viewBy
                };
                if (isEntityFilterable && selectedEntityIds.length > 0) {
                    params.entity_ids = selectedEntityIds;
                }
                const response = await axiosInstance.get('/crud/schedule/reporting', {
                    params,
                    // FastAPI expects repeated "entity_ids=a&entity_ids=b", not axios's
                    // default "entity_ids[]=a&entity_ids[]=b" bracket notation.
                    paramsSerializer: { indexes: null }
                });
                setReportData(response.data || null);
            } catch (error) {
                setReportData(null);
                showError(error?.response?.data?.detail || 'Failed to load reporting matrix.');
            } finally {
                setLoadingReport(false);
            }
        };

        fetchReport();
    }, [selectedCurriculum, viewBy, isEntityFilterable, selectedEntityIds, showError]);

    const reportRows = reportData?.rows || [];
    const reportColumns = reportData?.columns || [];
    const reportCells = reportData?.cells || {};

    const exportRows = useMemo(() => {
        return reportRows.map((row) => {
            const exportRow = {
                [ROW_LABELS[viewBy] || 'Row']: row.label
            };

            reportColumns.forEach((column) => {
                const cell = reportCells?.[row.id]?.[column.id];
                const current = cell?.currentTotalAvailability || 0;
                const original = cell?.originalTotalAvailability || 0;
                exportRow[column.label] = `${current}/${original}`;
            });

            return exportRow;
        });
    }, [reportCells, reportColumns, reportRows, viewBy]);

    const exportColumns = useMemo(() => {
        const labelKey = ROW_LABELS[viewBy] || 'Row';
        return [
            { key: labelKey, label: labelKey },
            ...reportColumns.map((column) => ({ key: column.label, label: column.label }))
        ];
    }, [reportColumns, viewBy]);

    const handleExport = (format) => {
        if (!reportRows.length || !reportColumns.length) {
            showError('No reporting data available to export.');
            return;
        }

        const title = `${reportData?.curriculum_name || selectedCurriculumOption?.name || 'Curriculum'} - ${ROW_LABELS[viewBy] || viewBy} by Rotation`;
        const filename = `schedule_reporting_${selectedCurriculum || 'curriculum'}_${viewBy}`;
        exportRenderedTableData(exportRows, exportColumns, title, filename, format);
    };

    const rowHeaderLabel = ROW_LABELS[viewBy] || 'Rows';
    const firstColumnWidth = reportColumns.length > 0 ? '36%' : '100%';
    const rotationColumnWidth = reportColumns.length > 0 ? `${(64 / reportColumns.length).toFixed(4)}%` : '0%';

    const modalPopup = selectedCell?.cell?.popup;
    const summaryCurrent = selectedCell?.cell?.currentTotalAvailability || 0;
    const summaryOriginal = selectedCell?.cell?.originalTotalAvailability || 0;
    const modalTitle = getPopupTitle(modalPopup?.type);
    const modalEntityLabel = getPopupEntityLabel(modalPopup?.type);

    return (
        <div className="view-admin-schedules-page">
            <div className="view-admin-schedules-header">
                <div className="view-admin-schedules-header-inner">
                    <div className="view-admin-schedules-header-left">
                        <div className="view-admin-schedules-header-icon">📊</div>
                        <div>
                            <h1 className="view-admin-schedules-title">Schedule Reporting</h1>
                            <p className="view-admin-schedules-subtitle">View schedules by a single dimension, with rotations fixed on the columns.</p>
                        </div>
                    </div>
                    <div className="view-admin-schedules-header-actions">
                        <ExportButton
                            onExport={handleExport}
                            formats={['CSV', 'PDF']}
                            disabled={!reportRows.length || !reportColumns.length || loadingReport}
                            customLabel="Export Showing Tables"
                        />
                    </div>
                </div>
            </div>

            <div className="modern-schedule-controls" style={{ marginBottom: '1.5rem' }}>
                <div className="control-group control-group--curriculum">
                    <label className="control-label">📚 Curriculum</label>
                    <Select
                        isLoading={loadingCurriculums}
                        value={selectedCurriculumOption ? { value: selectedCurriculumOption._id, label: selectedCurriculumOption.name || selectedCurriculumOption._id } : null}
                        onChange={(option) => setSelectedCurriculum(option?.value || '')}
                        options={(curriculums || []).map((curriculum) => ({
                            value: curriculum._id,
                            label: curriculum.name || curriculum._id
                        }))}
                        placeholder={loadingCurriculums ? 'Loading…' : 'Select curriculum...'}
                        isClearable
                        isDisabled={loadingCurriculums || curriculums.length === 0}
                        styles={{
                            container: (base) => ({ ...base, minWidth: 0, maxWidth: '100%' }),
                            control: (base) => ({ ...base, minHeight: 44 })
                        }}
                    />
                </div>

                <div className="control-group control-group--curriculum">
                    <label className="control-label">View By</label>
                    <Select
                        value={VIEW_BY_OPTIONS.find((item) => item.value === viewBy)}
                        onChange={(option) => setViewBy(option?.value || 'speciality')}
                        options={VIEW_BY_OPTIONS}
                        styles={{
                            container: (base) => ({ ...base, minWidth: 0, maxWidth: '100%' }),
                            control: (base) => ({ ...base, minHeight: 44 })
                        }}
                    />
                </div>

                {isEntityFilterable && (
                    <div className="control-group control-group--multi-select">
                        <label className="control-label">
                            {viewBy === 'student' ? '🎓 Students' : '👨‍⚕️ Preceptors'}
                        </label>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            isLoading={loadingEntityOptions}
                            isDisabled={loadingEntityOptions || entityOptions.length === 0}
                            value={entityOptions.filter((option) => selectedEntityIds.includes(option.value))}
                            onChange={(selected) => setSelectedEntityIds(selected ? selected.map((opt) => opt.value) : [])}
                            options={entityOptions}
                            placeholder={loadingEntityOptions ? 'Loading…' : `Select ${viewBy}(s)... (all shown if none selected)`}
                            styles={{
                                control: (base) => ({ ...base, minHeight: '44px', borderRadius: '12px', borderWidth: '2px', borderColor: '#e5e7eb', fontSize: '0.95rem', fontWeight: '500' }),
                                multiValue: (base) => ({ ...base, backgroundColor: '#3b82f6', borderRadius: '8px' }),
                                multiValueLabel: (base) => ({ ...base, color: 'white', fontWeight: '500' }),
                                multiValueRemove: (base) => ({ ...base, color: 'white', ':hover': { backgroundColor: '#dc2626', color: 'white' } })
                            }}
                        />
                    </div>
                )}
            </div>

            {loadingReport ? (
                <LoadingSpinner />
            ) : reportRows.length > 0 && reportColumns.length > 0 ? (
                <div style={tableStyles.wrapper}>
                    <div style={tableStyles.tableContainer}>
                        <table style={tableStyles.table} aria-label="Schedule reporting matrix">
                            <colgroup>
                                <col style={{ width: firstColumnWidth }} />
                                {reportColumns.map((column) => (
                                    <col key={column.id} style={{ width: rotationColumnWidth }} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th colSpan={reportColumns.length + 1} style={tableStyles.headerShell}>
                                        <div
                                            style={{
                                                ...tableStyles.headerShellGrid,
                                                gridTemplateColumns: `${firstColumnWidth} minmax(0, 1fr)`
                                            }}
                                        >
                                            <div style={tableStyles.headerLeftBlock}>{rowHeaderLabel}</div>
                                            <div style={tableStyles.headerRightBlock}>
                                                <div style={tableStyles.headerRightTitle}>Rotation</div>
                                                <div
                                                    style={{
                                                        ...tableStyles.headerRightNumbers,
                                                        gridTemplateColumns: `repeat(${reportColumns.length}, minmax(0, 1fr))`
                                                    }}
                                                >
                                                    {reportColumns.map((column, index) => (
                                                        <div
                                                            key={`rotation-${column.id}`}
                                                            style={{
                                                                ...tableStyles.headerRightNumber,
                                                                borderLeft: index === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.18)'
                                                            }}
                                                            title={column.label}
                                                        >
                                                            {String(column.label || '').replace(/^Rotation\s*/i, '')}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportRows.map((row, rowIdx) => (
                                    <tr key={row.id}>
                                        <th
                                            style={{
                                                ...tableStyles.rowHeader,
                                                background: rowIdx % 2 === 0 ? '#ffffff' : '#f9fbff'
                                            }}
                                            title={row.label}
                                        >
                                            {row.label}
                                        </th>
                                        {reportColumns.map((column) => {
                                            const cell = reportCells?.[row.id]?.[column.id];
                                            const current = cell?.currentTotalAvailability || 0;
                                            const original = cell?.originalTotalAvailability || 0;
                                            const popup = cell?.popup;
                                            const clickable = hasPopupData(popup);
                                            const isSelected =
                                                selectedCell &&
                                                selectedCell.row.id === row.id &&
                                                selectedCell.column.id === column.id;

                                            let cellBackground = clickable ? '#f8fbff' : '#ffffff';
                                            let border = '1px solid transparent';
                                            if (isSelected) {
                                                cellBackground = '#edf4ff';
                                                border = '1px solid #5c87b7';
                                            }

                                            return (
                                                <td key={`${row.id}-${column.id}`} style={tableStyles.dataCell}>
                                                    <button
                                                        type="button"
                                                        onClick={() => clickable && setSelectedCell({ row, column, cell })}
                                                        disabled={!clickable}
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '39px',
                                                            borderRadius: '6px',
                                                            border,
                                                            background: cellBackground,
                                                            color: clickable ? palette.text : '#96a3b4',
                                                            fontSize: '0.78rem',
                                                            fontWeight: 700,
                                                            cursor: clickable ? 'pointer' : 'default',
                                                            padding: '0.15rem 0.25rem',
                                                            outline: 'none',
                                                            boxShadow: clickable ? 'inset 0 1px 0 rgba(255, 255, 255, 0.85)' : 'none',
                                                            transition: 'all 0.18s ease'
                                                        }}
                                                        title={clickable ? `${row.label} / ${column.label}` : 'No details in this cell'}
                                                    >
                                                        {`${current}/${original}`}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div style={{ padding: '1.25rem', border: '1px solid #e2e8f0', borderRadius: '14px', background: '#f8fafc', color: '#475569' }}>
                    No reporting data available for the selected curriculum.
                </div>
            )}

            {selectedCell && createPortal(
                <div style={modalStyles.backdrop} onClick={() => setSelectedCell(null)} role="dialog" aria-modal="true" aria-labelledby="schedule-reporting-modal-title">
                    <div style={modalStyles.card} onClick={(event) => event.stopPropagation()}>
                        <div style={modalStyles.head}>
                            <div>
                                <h4 id="schedule-reporting-modal-title" style={modalStyles.headTitle}>{modalTitle}</h4>
                                <p style={modalStyles.headSub}>{modalTitle}</p>
                            </div>
                            <button type="button" onClick={() => setSelectedCell(null)} style={modalStyles.closeButton} aria-label="Close popup" title="Close">
                                &times;
                            </button>
                        </div>

                        <div style={modalStyles.summary}>
                            <div style={modalStyles.summaryCard}>
                                <div style={modalStyles.summaryLabel}>Availability</div>
                                <div style={modalStyles.summaryValue}>{summaryCurrent}/{summaryOriginal}</div>
                            </div>
                            <div style={modalStyles.summaryCard}>
                                <div style={modalStyles.summaryLabel}>{modalEntityLabel}</div>
                                <div style={modalStyles.summaryValue}>{selectedCell.row.label}</div>
                            </div>
                            <div style={modalStyles.summaryCard}>
                                <div style={modalStyles.summaryLabel}>Rotation</div>
                                <div style={modalStyles.summaryValue}>{selectedCell.column.label}</div>
                            </div>
                        </div>

                        <div style={modalStyles.body}>
                            {modalPopup?.type === 'speciality' || modalPopup?.type === 'institution'
                                ? renderGroupedPopup(modalPopup)
                                : null}
                            {modalPopup?.type === 'preceptor' ? renderPreceptorPopup(modalPopup) : null}
                            {modalPopup?.type === 'student' ? renderStudentPopup(modalPopup) : null}
                            {!modalPopup?.type ? <div style={modalStyles.emptyState}>No details available for this cell.</div> : null}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default ScheduleReportingPage;
